import {
    Cancel,
    ErrorOutline,
    HourglassTop,
    Verified,
} from "@mui/icons-material";
import {
    ApprovalEvent,
    ApprovalStatus,
    type LLMClassifierApprovalEmailNotification,
    type LLMClassifierApprovalEventSummary,
} from "models/LLMs/LLMClassifierApproval";

/** Set by the approval emails so the builder can open the notice matching the email that was clicked. */
export const APPROVAL_NOTICE_SEARCH_PARAM = "approvalNotice";

const APPROVAL_NOTICE_EVENTS: Record<string, ApprovalEvent> = {
    "approval-requested": ApprovalEvent.ApprovalRequested,
    approved: ApprovalEvent.Approved,
    "changes-requested": ApprovalEvent.ChangesRequested,
    rejected: ApprovalEvent.Rejected,
};

export function approvalEventFromNoticeParam(
    value: string | null | undefined,
): ApprovalEvent | undefined {
    if (!value) return undefined;
    return APPROVAL_NOTICE_EVENTS[value];
}

export function effectiveApprovalStatus(
    status?: ApprovalStatus,
): ApprovalStatus {
    return status ?? ApprovalStatus.Draft;
}

export function approvalEmailNotificationWarning(
    event: ApprovalEvent,
    notification: LLMClassifierApprovalEmailNotification,
): string | undefined {
    if (event === ApprovalEvent.ApprovalRequestCanceled) return undefined;

    if (notification.targetedUserCount === 0) {
        // A failure with nobody targeted means sending broke before recipients were found.
        if (notification.failedAddressCount > 0) {
            return `${approvalEventLabel(event)}, but notification emails could not be sent.`;
        }
        return event === ApprovalEvent.ApprovalRequested
            ? "Approval requested, but no eligible approvers could be notified by email."
            : `${approvalEventLabel(event)}, but the approval requester could not be notified by email.`;
    }

    const undelivered =
        notification.failedAddressCount +
        notification.missingAddressUserCount;
    if (undelivered === 0) return undefined;

    return `${approvalEventLabel(event)}, but email could not be delivered to ${undelivered} ${undelivered === 1 ? "recipient" : "recipients"}.`;
}

export function approvalNotificationToastMessages(
    event: ApprovalEvent,
    notification: LLMClassifierApprovalEmailNotification,
): { success: string; warning?: string } {
    return {
        success: approvalEventLabel(event),
        warning: approvalEmailNotificationWarning(event, notification),
    };
}

/** Icon and color for each approval event, shared by every approval dialog. */
export const APPROVAL_EVENT_ICONS: Record<
    ApprovalEvent,
    { Icon: typeof Verified; color: string }
> = {
    [ApprovalEvent.Approved]: { Icon: Verified, color: "success.700" },
    [ApprovalEvent.Rejected]: { Icon: Cancel, color: "error.main" },
    [ApprovalEvent.ChangesRequested]: {
        Icon: ErrorOutline,
        color: "error.main",
    },
    [ApprovalEvent.ApprovalRequested]: {
        Icon: HourglassTop,
        color: "warning.main",
    },
    [ApprovalEvent.ApprovalRequestCanceled]: { Icon: Cancel, color: "gray.500" },
};

export function approvalEventLabel(event: ApprovalEvent): string {
    switch (event) {
        case ApprovalEvent.ApprovalRequested:
            return "Approval requested";
        case ApprovalEvent.ApprovalRequestCanceled:
            return "Approval request canceled";
        case ApprovalEvent.Approved:
            return "Version approved";
        case ApprovalEvent.ChangesRequested:
            return "Changes requested";
        case ApprovalEvent.Rejected:
            return "Version rejected";
        default:
            return "Approval updated";
    }
}

export function approvalStatusLabel(status?: ApprovalStatus): string {
    switch (effectiveApprovalStatus(status)) {
        case ApprovalStatus.AwaitingApproval:
            return "Awaiting Approval";
        case ApprovalStatus.ChangesRequested:
            return "Changes Requested";
        case ApprovalStatus.Approved:
            return "Approved";
        case ApprovalStatus.Rejected:
            return "Rejected";
        default:
            return "Draft";
    }
}

export function isApprovalRequester(
    request: LLMClassifierApprovalEventSummary | undefined,
    currentUserEmail: string | undefined,
): boolean {
    return (
        !!request?.createdBy &&
        !!currentUserEmail &&
        request.createdBy.trim().toLowerCase() ===
            currentUserEmail.trim().toLowerCase()
    );
}

/**
 * Whether this user can act on an open request: an approver, not its requester, and either named on
 * it or covered by a request that named no one.
 */
export function canReviewApprovalRequest(args: {
    canReview: boolean;
    isRequester: boolean;
    request: LLMClassifierApprovalEventSummary | undefined;
    currentUserId: string | undefined;
}): boolean {
    return (
        args.canReview &&
        !args.isRequester &&
        (!args.request?.requestedApprovers.length ||
            args.request.requestedApprovers.some(
                (reviewer) => reviewer.userId === args.currentUserId,
            ))
    );
}
